import { Injectable } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { LoggerService } from '../../../shared/services/logger.service';

@Injectable({ providedIn: 'root' })
export class PushNotificationService {
  private readonly _isSubscribed$ = new BehaviorSubject<boolean>(false);
  readonly isSubscribed$ = this._isSubscribed$.asObservable();

  /** True only when feature flag is on AND the browser/SW supports push. */
  get canSubscribe(): boolean {
    return environment.pushNotificationsEnabled && this.swPush.isEnabled;
  }

  get isSubscribed(): boolean {
    return this._isSubscribed$.value;
  }

  constructor(
    private readonly swPush: SwPush,
    private readonly router: Router,
    private readonly http: HttpClient,
    private readonly logger: LoggerService
  ) {
    // Only wire up push tracking when the feature is enabled AND the browser
    // actually supports Push. On Chrome-for-iOS / Safari webviews the service worker
    // IS registered (swPush.isEnabled === true) but registration.pushManager is
    // undefined, so swPush.subscription throws "undefined is not an object
    // (evaluating '…getSubscription')". Gating on canSubscribe avoids that entirely.
    if (this.canSubscribe) {
      this.trackSubscriptionState();
      this.handleNotificationClicks();
    }
  }

  /**
   * Request push permission and subscribe.
   * No-op when feature flag is off or browser doesn't support it.
   *
   * When enabling:
   *   1. Set environment.pushNotificationsEnabled = true
   *   2. Set environment.vapidPublicKey = '<backend VAPID public key>'
   *   3. Uncomment the backend POST below
   */
  async requestSubscription(): Promise<void> {
    if (!this.canSubscribe) return;

    const subscription = await this.swPush.requestSubscription({
      serverPublicKey: environment.vapidPublicKey
    });

    // TODO(push-backend): POST subscription to backend when endpoint is ready
    // const apiUrl = environment.apiRoot.replace(/\/+$/, '');
    // await firstValueFrom(this.http.post(`${apiUrl}/announcements/push-subscription`, subscription));

    this.logger.errorWithContext('PushNotificationService', 'Subscription created but backend endpoint not yet wired', subscription);
  }

  async unsubscribe(): Promise<void> {
    if (!this.swPush.isEnabled) return;
    try {
      await this.swPush.unsubscribe();
    } catch (err) {
      this.logger.errorWithContext('PushNotificationService', 'Failed to unsubscribe from push notifications', err);
    }
  }

  private trackSubscriptionState(): void {
    this.swPush.subscription.subscribe({
      next: sub => this._isSubscribed$.next(sub !== null),
      // Some browsers (Chrome-for-iOS / Safari webviews) register a service worker
      // but expose no PushManager, so this stream errors. Fail soft instead of
      // surfacing an unhandled TypeError to the global error handler.
      error: err => this.logger.errorWithContext(
        'PushNotificationService',
        'Push subscription unavailable in this browser',
        err
      ),
    });
  }

  /**
   * Handle clicks on push notifications — navigates to the announcement.
   * The backend push payload must include: { data: { announcementId: number } }
   */
  private handleNotificationClicks(): void {
    this.swPush.notificationClicks.subscribe(({ notification }) => {
      const announcementId = (notification.data as { announcementId?: number } | undefined)?.announcementId;
      if (announcementId) {
        void this.router.navigate(['/announcements', announcementId]);
      }
    });
  }
}
